/**
 * Clientes do Gerenciador de Produção — rota /legado/clientes/*.
 *
 * Lista e ficha leem a planilha que o DadosContext já carregou (sem backend
 * do ERP novo) e, para quem tem `central_projetos:view`, cruzam com a
 * Central de Projetos. Isolado da rota /clientes/* do cadastro do ERP novo.
 */
import React, { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Route, Routes, useParams } from "react-router-dom";

import { EmptyStateErro } from "../../../components/EmptyState";
import { useDados } from "../../../context/DadosContext";
import { chegandoPorTransicao } from "./comum/transicao";
import { idDaRota, montarClientes } from "./dados/montarClientes";
import FichaDoCliente from "./Ficha";
import EsqueletoDaLista from "./Lista/Esqueleto";
import ListaDeClientes from "./Lista";
import "../../../styles/canonical/redesign-v2.css";
import "./clientes-legado.css";
import "./clientes-ficha.css";

function useClientesDaPlanilha() {
  const { dados, erro, initialLoading } = useDados();
  const clientes = useMemo(() => montarClientes(dados), [dados]);
  return { clientes, erro, carregando: initialLoading };
}

/** Sem a cascata de entrada quando a tela chega por View Transition (é ela que anima). */
function Entrada({ children }) {
  const [animar] = useState(() => !chegandoPorTransicao());
  return <AnimatePresence initial={animar}>{children}</AnimatePresence>;
}

function Lista() {
  const { clientes, erro, carregando } = useClientesDaPlanilha();
  const [animar] = useState(() => !chegandoPorTransicao());
  if (carregando) return <EsqueletoDaLista />;
  if (erro) return <EmptyStateErro erro={erro} />;
  return (
    <Entrada>
      <ListaDeClientes key="lista" clientes={clientes} animarEntrada={animar} />
    </Entrada>
  );
}

function Ficha() {
  const { clienteId } = useParams();
  const { clientes, erro, carregando } = useClientesDaPlanilha();
  const cliente = useMemo(
    () => clientes.find((c) => c.id === idDaRota(clienteId)) || null,
    [clientes, clienteId],
  );
  if (carregando) return <EsqueletoDaLista />;
  if (erro) return <EmptyStateErro erro={erro} />;
  if (!cliente) return <EmptyStateErro titulo="Cliente não encontrado" erro="Esse cliente não está na planilha carregada agora." />;
  return (
    <Entrada>
      <FichaDoCliente key={cliente.id} cliente={cliente} />
    </Entrada>
  );
}

export default function ClientesLegado() {
  return (
    <Routes>
      <Route index element={<Lista />} />
      <Route path=":clienteId" element={<Ficha />} />
    </Routes>
  );
}
